import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Icon } from "@iconify/react";
import { useFormBuilder } from "../../utils/useFormBuilder";
import { renderErrorDot, validateForm } from "../../utils/useValidator";
import { getTasks, getTasksByAsset, updateTasks } from "../../api/tasksAPI";
import { useSetting } from "../../utils/AppSettingsContext";
import {
  getTaskFormFields,
  getDynamicTaskFormFields,
} from "./taskFieldConfigs";
import { buildSchedulingFields } from "./taskFieldConfigs";
import { getAssetMetersByAssetId } from "../../api/asset-meterAPI";
import { fetchData } from "../../utils/apiHelpers";
import { getAll, getById } from "../../api";
import { getSchedulingGroups } from "../../api/scheduling-groupAPI";
import { estimateMeterReading } from "../../api/meter-readingsAPI";
import { getTaskCycleGroupById } from "../../api/task-cycle-groupAPI";
import CycleTasksEditor from "./CycleTasksEditor";
import { CURRENT_TASK_PLACEHOLDER, createCycleRow, resolveAssetMeterId } from "./taskCycleUtils";
import { useValidator } from "../../utils/useValidator";

const baseSchedulingTypeOptions = [
  { label: "Simple", value: "simple" },
  { label: "Suppression", value: "suppression" },
  { label: "Cycle", value: "cycle" },
];

const schedulingWindowOptions = [
  { value: 24, label: "1 Day" },
  { value: 48, label: "2 Days" },
  { value: 72, label: "3 Days" },
  { value: 120, label: "5 Days" },
  { value: 168, label: "1 Week" },
  { value: 730, label: "1 Month" },
  { value: 1460, label: "2 Months" },
  { value: 2190, label: "3 Months" },
  { value: 2920, label: "4 Months" },
  { value: 4380, label: "6 Months" },
  { value: 8760, label: "1 Year" },
];

const schedulingPriorityOptions = [
  { value: 1, label: "1" },
  { value: 2, label: "2" },
  { value: 3, label: "3" },
  { value: 4, label: "4" },
  { value: 5, label: "5" },
];

const forecastBasisOptions = [
  { value: "lastdone", label: "Last Done" },
  // { value: "lastscheduled", label: "Last Scheduled" },
  { value: "fixed", label: "Fixed" },
];

const initialTaskState = {
  planningTask: 0,
  calendarBased: 1,
  schedulingType: "simple",
  schedulingPriority: 1,
  schedulingWindow: 48,
  cycleGroupId: null,
  assetId: null,
  lastDone: "",
  forecastBasis: "lastdone",
};

const toDateInputValue = (isoValue) => {
  if (!isoValue) return "";
  const date = new Date(isoValue);
  if (Number.isNaN(date.getTime())) return "";
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const TaskEditModal = ({ taskId = 0, show = false, onClose, onSaved, showCostFields = false } = {}) => {
  const showTaskRefSource = useSetting("show_task_ref_source");
  const [taskData, rawSetTaskData] = useState(initialTaskState);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [assetMeterOptions, setAssetMeterOptions] = useState([]);
  const [assetMeters, setAssetMeters] = useState([]);
  const [assetTasks, setAssetTasks] = useState([]);
  const [schedulingGroups, setSchedulingGroups] = useState([]);
  const [isLastDoneLocked, setIsLastDoneLocked] = useState(true);
  const [showLastDoneWarning, setShowLastDoneWarning] = useState(false);
  const [latestMeterReading, setLatestMeterReading] = useState(null);
  const [cycleForm, setCycleForm] = useState({ name: "" });
  const [cycleRows, setCycleRows] = useState([]);
  const [cycleRowErrors, setCycleRowErrors] = useState({});
  const [isCycleLoading, setIsCycleLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("general");
  const [suppressionGroupEdits, setSuppressionGroupEdits] = useState({});
  const [plantRateMeasurementUnitOption, setPlantRateMeasurementUnitOption] = useState(null);

  const schedulingTypeOptions = useMemo(
    () =>
      Boolean(taskData?.planningTask)
        ? baseSchedulingTypeOptions
        : baseSchedulingTypeOptions.filter((opt) => opt.value === "simple"),
    [taskData?.planningTask]
  );

  const taskFormFields = useMemo(() => getTaskFormFields({ showCostFields }), [showCostFields]);
  const dynamicTaskFormFields = useMemo(
    () => getDynamicTaskFormFields(taskFormFields, showTaskRefSource),
    [taskFormFields, showTaskRefSource]
  );

  const cycleGroupFields = useMemo(
    () => [
      {
        field: "name",
        label: "Cycle Group Name",
        type: "text",
        size: 12,
        required: taskData?.schedulingType === "cycle",
        tooltip: "A Cycle Group will schedule tasks is order T1 - T2 - T3 and then repeat.",
      },
    ],
    [taskData?.schedulingType]
  );

  const {
    errors: cycleFormErrors,
    setErrors: setCycleFormErrors,
    validate: validateCycleGroupField,
  } = useValidator(cycleGroupFields, cycleForm);

  const sanitizeInputValues = useCallback(
    (rawData = {}) => {
      const base = rawData && typeof rawData === "object" ? rawData : {};
      const sanitized = { ...initialTaskState, ...base };
      const textLikeTypes = new Set(["text", "number", "textarea", "date", "datetime"]);

      const applyFieldDefaults = (fields) => {
        fields.forEach(({ field, type }) => {
          if (!field || field.startsWith("General") || !textLikeTypes.has(type)) return;
          const currentValue = sanitized[field];
          sanitized[field] = currentValue ?? "";
        });
      };

      applyFieldDefaults(dynamicTaskFormFields);

      const schedulingFieldDefs = buildSchedulingFields({
        modalTask: sanitized,
        schedulingGroupOptions: schedulingGroups,
        assetMeterOptions,
        plantRateMeasurementUnitOption,
        schedulingTypeOptions,
        forecastBasisOptions,
        schedulingWindowOptions,
        schedulingPriorityOptions,
      });

      applyFieldDefaults(schedulingFieldDefs);

      sanitized.lastDone = sanitized.lastDone ?? "";

      return sanitized;
    },
    [dynamicTaskFormFields, schedulingGroups, assetMeterOptions, plantRateMeasurementUnitOption, schedulingTypeOptions]
  );

  const setTaskData = useCallback(
    (valueOrUpdater) => {
      rawSetTaskData((prev) => {
        const nextValue =
          typeof valueOrUpdater === "function" ? valueOrUpdater(prev) : valueOrUpdater;
        const safeNext = nextValue && typeof nextValue === "object" ? nextValue : {};
        return sanitizeInputValues(safeNext);
      });
    },
    [sanitizeInputValues]
  );

  const schedulingFields = useMemo(
    () =>
      buildSchedulingFields({
        modalTask: taskData,
        schedulingGroupOptions: schedulingGroups,
        assetMeterOptions,
        plantRateMeasurementUnitOption,
        schedulingTypeOptions,
        forecastBasisOptions,
        schedulingWindowOptions,
        schedulingPriorityOptions,
      }),
    [taskData, schedulingGroups, assetMeterOptions, plantRateMeasurementUnitOption, schedulingTypeOptions]
  );

  const { renderField: renderGeneralField } = useFormBuilder(
    dynamicTaskFormFields,
    taskData,
    setTaskData,
    errors
  );
  const { renderField: renderSchedulingField } = useFormBuilder(
    schedulingFields,
    taskData,
    setTaskData,
    errors
  );
  const { renderField: renderCycleGroupField } = useFormBuilder(
    cycleGroupFields,
    cycleForm,
    setCycleForm,
    cycleFormErrors
  );

  const generalTabHasError = useMemo(
    () => dynamicTaskFormFields.some(({ field }) => field && errors[field]),
    [dynamicTaskFormFields, errors]
  );

  const schedulingTabHasError = useMemo(
    () =>
      schedulingFields.some(({ field }) => field && errors[field]) ||
      Object.keys(cycleRowErrors).length > 0 ||
      Object.keys(cycleFormErrors).length > 0,
    [errors, schedulingFields, cycleRowErrors, cycleFormErrors]
  );

  useEffect(() => {
    setTaskData((prev) => prev);
  }, [sanitizeInputValues, setTaskData]);

  useEffect(() => {
    if (!show || !taskId) return;

    setIsLastDoneLocked(true);
    setShowLastDoneWarning(false);
    setLatestMeterReading(null);
    setLoading(true);
    const loadTask = async () => {
      try {
        const data = await getTasks(taskId);
        const normalised = {
          ...initialTaskState,
          ...data,
          id: data?.id,
          codeId: data?.codeId ?? data?.code?.id ?? null,
          modifierCodeId: data?.modifierCodeId ?? data?.modifierTask?.id ?? null,
          taskTypeId: data?.taskTypeId ?? data?.taskType?.id ?? null,
          defaultCostCentre:
            data?.defaultCostCentreId ?? data?.defaultCostCentre?.id ?? data?.defaultCostCentre ?? null,
          labourExpense: data?.labourExpense ?? data?.labourExpenseId ?? null,
          miscExpense: data?.miscExpense ?? data?.miscExpenseId ?? null,
          planningTask: data?.planningTask ?? 0,
          calendarBased: data?.calendarBased ?? 1,
          frequency: data?.frequency ?? 0,
          assetId: data?.assetId ?? data?.asset?.id ?? null,
          assetMeterId: data?.assetMeterId ?? data?.assetMeter?.id ?? null,
          statutory: data?.statutory ?? 0,
          schedulingType: data?.schedulingType || "simple",
          forecastBasis: data?.forecastBasis || "lastdone",
          schedulingGroupId: data?.schedulingGroupId ?? data?.schedulingGroup?.id ?? null,
          schedulingWindow: data?.schedulingWindow ?? 48,
          schedulingPriority: data?.schedulingPriority ?? 1,
          cycleGroupId: data?.cycleGroupId ?? data?.cycleGroup?.id ?? null,
          lastDone: data?.calendarBased === 1
            ? toDateInputValue(data?.lastDoneDate)
            : data?.lastDone !== undefined && data?.lastDone !== null
              ? String(data.lastDone)
              : "",
        };
        setTaskData(normalised);
      } catch (err) {
        console.error("Failed to load task", err);
        setTaskData(initialTaskState);
      } finally {
        setLoading(false);
      }
    };

    loadTask();
  }, [taskId, show, setTaskData]);

  useEffect(() => {
    if (!show) return;
    fetchData(getSchedulingGroups, {}, setSchedulingGroups);
  }, [show]);

  useEffect(() => {
    if (!show) return;
    const plantRateId = Number(taskData?.plantRateId);
    if (!Number.isFinite(plantRateId) || plantRateId <= 0) {
      setPlantRateMeasurementUnitOption(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const [plantRate, measurementUnits] = await Promise.all([
          fetchData(getById, { apiName: "plant-rates", id: plantRateId }),
          fetchData(getAll, { apiName: "measurement-units" }),
        ]);
        if (cancelled) return;
        const measurementUnitId = Number(plantRate?.measurementUnitId);
        if (!Number.isFinite(measurementUnitId) || measurementUnitId <= 0) {
          setPlantRateMeasurementUnitOption(null);
          return;
        }
        const selectedUnit = (measurementUnits || []).find((u) => Number(u.id) === measurementUnitId);
        const unitName = selectedUnit?.unitName || selectedUnit?.unitCode || String(measurementUnitId);
        setPlantRateMeasurementUnitOption((prev) => {
          if (prev?.value === measurementUnitId && prev?.label === unitName) {
            return prev;
          }
          return { value: measurementUnitId, label: unitName };
        });
        rawSetTaskData((prev) => {
          if (!prev) return prev;
          if (Number(prev.assetMeterId) === measurementUnitId) return prev;
          return { ...prev, assetMeterId: measurementUnitId };
        });
      } catch (err) {
        console.error("Failed to load plant rate measurement unit", err);
        if (!cancelled) setPlantRateMeasurementUnitOption(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [show, taskData?.plantRateId]);

  const assetId = useMemo(
    () =>
      taskData?.assetId !== undefined && taskData?.assetId !== null
        ? Number(taskData.assetId)
        : null,
    [taskData?.assetId]
  );

  useEffect(() => {
    if (!show) return;
    if (assetId === null || !Number.isFinite(assetId)) {
      setAssetTasks([]);
      setAssetMeterOptions([]);
      setAssetMeters([]);
      return;
    }

    let cancelled = false;

    const loadAssetData = async () => {
      try {
        const [tasks, meters] = await Promise.all([
          getTasksByAsset(assetId),
          getAssetMetersByAssetId(assetId),
        ]);

        if (cancelled) return;

        setAssetTasks(Array.isArray(tasks) ? tasks : []);

        const safeMeters = Array.isArray(meters)
          ? meters.filter((m) => Number(m.assetId) === assetId)
          : [];

        setAssetMeters(safeMeters);
        setAssetMeterOptions(
          safeMeters.map((meter) => ({
            value: meter.id,
            label: meter.meter?.name || "",
            unitCode: meter.meter?.measurementUnits?.unitCode || "",
            unitName: meter.meter?.measurementUnits?.unitName || "",
            meterId: meter.meterId ?? meter.meter?.id ?? meter.id,
          })),
        );
      } catch (err) {
        if (cancelled) return;
        console.error("Failed to load asset data", err);
        setAssetTasks([]);
        setAssetMeterOptions([]);
        setAssetMeters([]);
      }
    };

    loadAssetData();

    return () => {
      cancelled = true;
    };
  }, [show, assetId]);




  useEffect(() => {
    if (!taskData) return;
    if ((taskData.schedulingType === "cycle" || taskData.schedulingType === "suppression") && taskData.planningTask !== 1) {
      setTaskData((prev) => (prev ? { ...prev, planningTask: 1 } : prev));
    }
  }, [setTaskData, taskData, taskData?.planningTask, taskData?.schedulingType]);

  const selectedAssetMeter = useMemo(() => {
    if (!taskData) return null;
    const rawAssetMeterId = taskData?.assetMeterId;
    if (rawAssetMeterId === undefined || rawAssetMeterId === null || rawAssetMeterId === "") {
      return null;
    }
    const numericId = Number(rawAssetMeterId);
    if (Number.isNaN(numericId)) {
      return null;
    }
    const directMatch = assetMeters.find((meter) => Number(meter.id) === numericId);
    if (directMatch) {
      return directMatch;
    }
    return assetMeters.find((meter) => Number(meter.meterId) === numericId) || null;
  }, [assetMeters, taskData?.assetMeterId]);

  const lastDoneUnitName = selectedAssetMeter?.meter?.measurementUnits?.unitName;
  const lastDoneLabel = useMemo(
    () => (lastDoneUnitName ? `Last Known Change in ${lastDoneUnitName}` : "Last Known Change"),
    [lastDoneUnitName]
  );

  const baseMeterId = useMemo(() => {
    const meterFromTask = resolveAssetMeterId(taskData);
    if (meterFromTask !== null) return meterFromTask;
    const selectedMeterId = Number(selectedAssetMeter?.id);
    return Number.isFinite(selectedMeterId) ? selectedMeterId : null;
  }, [selectedAssetMeter?.id, taskData]);

  const formatTaskLabel = useCallback((task) => {
    if (!task) return "";
    const codeValue = task?.code?.name ?? "";
    const codePart = codeValue ? String(codeValue) : "";
    const namePart = task?.name ? String(task.name) : "";
    const combined = [codePart, namePart].filter(Boolean).join(" - ");
    if (combined) return combined;
    if (namePart) return namePart;
    if (task?.id) return `Task ${task.id}`;
    return "";
  }, []);

  const baseTaskLabel = useMemo(
    () => formatTaskLabel(taskData) || "This Task",
    [formatTaskLabel, taskData]
  );

  const taskMeterMap = useMemo(() => {
    const map = new Map();
    assetTasks.forEach((task) => {
      const taskId = Number(task.id);
      if (!Number.isFinite(taskId)) return;
      const meterId = resolveAssetMeterId(task);
      if (meterId !== null) {
        map.set(taskId, meterId);
      }
    });
    return map;
  }, [assetTasks]);

  const resetCycleState = useCallback(() => {
    setCycleForm({ name: "" });
    setCycleRows([]);
    setCycleRowErrors({});
    setCycleFormErrors({});
  }, [setCycleFormErrors]);

  const updateCycleState = useCallback(
    (name, rows) => {
      const normalizedName = typeof name === "string" ? name : "";
      const normalizedRows =
        Array.isArray(rows) && rows.length
          ? rows.map((row) => createCycleRow(row))
          : [];
      setCycleForm({ name: normalizedName });
      setCycleRows(normalizedRows);
      setCycleRowErrors({});
      setCycleFormErrors({});
    },
    [setCycleFormErrors]
  );

  const buildDefaultCycleRows = useCallback((task) => {
    const baseRow = createCycleRow({
      taskId:
        task?.id !== undefined && task?.id !== null
          ? task.id
          : CURRENT_TASK_PLACEHOLDER,
      meterId: resolveAssetMeterId(task),
    });
    const additionalRow = createCycleRow();
    return [baseRow, additionalRow];
  }, []);

  const loadCycleRecords = useCallback(
    async (task) => {
      if (!task) {
        updateCycleState("", []);
        return;
      }

      if (!task.id) {
        updateCycleState("", buildDefaultCycleRows(task));
        return;
      }

      if (!task.cycleGroupId) {
        updateCycleState("", buildDefaultCycleRows(task));
        return;
      }

      setIsCycleLoading(true);
      try {
        const group = await getTaskCycleGroupById(task.cycleGroupId);
        const groupName = typeof group?.name === "string" ? group.name : "";
        const records = Array.isArray(group?.tasks) ? group.tasks : [];

        if (!records.length) {
          updateCycleState(groupName, buildDefaultCycleRows(task));
          return;
        }

        const sortedRows = records
          .slice()
          .sort(
            (a, b) => (Number(a.order) || 0) - (Number(b.order) || 0) || (Number(a.id) || 0) - (Number(b.id) || 0)
          )
          .map((row) =>
            createCycleRow({
              id: row.id,
              clientId: `cycle-${row.id}`,
              taskId: row.taskId ?? null,
              interval: row.interval ?? row.startValue ?? "",
              meterId: row.meterId ?? null,
            })
          );

        updateCycleState(groupName, sortedRows);
      } catch (err) {
        console.error("Error loading cycle group:", err);
        updateCycleState("", buildDefaultCycleRows(task));
      } finally {
        setIsCycleLoading(false);
      }
    },
    [buildDefaultCycleRows, updateCycleState]
  );

  useEffect(() => {
    if (!show) {
      setErrors({});
      setTaskData({ ...initialTaskState });
      setIsLastDoneLocked(true);
      setShowLastDoneWarning(false);
      setLatestMeterReading(null);
      resetCycleState();
      setSuppressionGroupEdits({});
    }
  }, [resetCycleState, show, setTaskData]);

  const taskIdNumber = taskData?.id ? Number(taskData.id) : null;
  const cycleGroupId = taskData?.cycleGroupId ?? null;
  const schedulingType = taskData?.schedulingType;

  useEffect(() => {
    if (!show) return;
    if (schedulingType !== "cycle") return;

    // no task yet, so just initialise defaults instead of calling API
    if (!taskIdNumber && !cycleGroupId) {
      loadCycleRecords({
        ...taskData,
        id: null,
        cycleGroupId: null,
      });
      return;
    }

    loadCycleRecords({
      ...taskData,
      id: taskIdNumber,
      cycleGroupId,
    });
  }, [show, schedulingType, taskIdNumber, cycleGroupId, loadCycleRecords]);


  const cycleTaskOptions = useMemo(() => {
    // if (!baseMeterId) return [];
    const options = assetTasks
      .filter((task) => {
        if (task.calendarBased === 1 && task.planningTask === 1) {
          return true;
        }

        const meterId = resolveAssetMeterId(task);
        // if (meterId === null) return false;
        if (task?.schedulingType === "suppression") return false;
        return Number(meterId) === Number(baseMeterId);
      })
      .map((task) => ({
        value: String(task.id),
        label: formatTaskLabel(task),
      }));

    if (taskData) {
      const currentValue =
        taskData.id !== undefined && taskData.id !== null
          ? String(taskData.id)
          : CURRENT_TASK_PLACEHOLDER;
      if (!options.some((opt) => opt.value === currentValue)) {
        options.unshift({ value: currentValue, label: baseTaskLabel });
      }
    }
    return options;
  }, [assetTasks, baseMeterId, baseTaskLabel, formatTaskLabel, taskData]);


  const meterId = useMemo(() => {
    if (!selectedAssetMeter) return null;
    const raw = selectedAssetMeter.meterId ?? selectedAssetMeter.meter?.id;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  }, [selectedAssetMeter]);

  const taskIdNumberRaw = useMemo(() => {
    const raw = taskData?.id ?? taskId;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  }, [taskData?.id, taskId]);

  const suppressionGroupTasks = useMemo(() => {
    if (taskData?.schedulingType !== "suppression") return [];
    const groupId = Number(taskData?.schedulingGroupId);
    if (!Number.isFinite(groupId)) return [];

    return assetTasks
      .filter((task) => {
        const isSameGroup = Number(task?.schedulingGroupId) === groupId;
        const isDifferentTask = Number(task?.id) !== Number(taskIdNumberRaw);
        return isSameGroup && isDifferentTask;
      })
      .map((task) => {
        const taskId = Number(task?.id);
        const edits = suppressionGroupEdits[taskId] || {};
        return {
          id: taskId,
          name: task?.name || `Task ${taskId}`,
          schedulingWindow:
            edits.schedulingWindow ?? task?.schedulingWindow ?? schedulingWindowOptions[0]?.value ?? 24,
          schedulingPriority:
            edits.schedulingPriority ?? task?.schedulingPriority ?? schedulingPriorityOptions[0]?.value ?? 1,
        };
      });
  }, [assetTasks, suppressionGroupEdits, taskData?.schedulingGroupId, taskData?.schedulingType, taskIdNumberRaw]);

  const handleSuppressionGroupTaskChange = useCallback((taskIdValue, field, value) => {
    const taskId = Number(taskIdValue);
    if (!Number.isFinite(taskId)) return;
    const nextValue = Number(value);

    setSuppressionGroupEdits((prev) => ({
      ...prev,
      [taskId]: {
        ...(prev[taskId] || {}),
        [field]: Number.isFinite(nextValue) ? nextValue : value,
      },
    }));
  }, []);

  const assetIdNumber = useMemo(() => {
    const raw = taskData?.assetId;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  }, [taskData?.assetId]);

  useEffect(() => {
    if (!show) return;
    if (!meterId || !taskIdNumberRaw || !assetIdNumber) {
      setLatestMeterReading(null);
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        const estimate = await estimateMeterReading({
          assetId: assetIdNumber,
          taskId: taskIdNumberRaw,
          meterId,
          providedDate: new Date().toISOString(),
        });
        if (cancelled) return;

        const value =
          estimate && estimate.estimatedValue !== undefined && estimate.estimatedValue !== null
            ? Number(estimate.estimatedValue)
            : null;
        setLatestMeterReading(Number.isFinite(value) ? value : null);
      } catch (err) {
        if (!cancelled) {
          console.error("Error fetching estimated meter reading", err);
          setLatestMeterReading(null);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [show, meterId, taskIdNumber, assetIdNumber]);


  const handleLastDoneToggle = () => {
    if (isLastDoneLocked) {
      setIsLastDoneLocked(false);
      setShowLastDoneWarning(true);
    } else {
      setIsLastDoneLocked(true);
      setShowLastDoneWarning(false);
    }
  };

  const handleLastDoneChange = (event) => {
    const { value } = event.target;
    setTaskData((prev) => ({ ...prev, lastDone: value ?? "" }));
    setErrors((prev) => {
      if (!prev.lastDone) return prev;
      const next = { ...prev };
      delete next.lastDone;
      return next;
    });
  };

  const validateFrequency = () => {
    const freq = Number(taskData.frequency) || 0;
    if (taskData.planningTask) {
      if (taskData.schedulingType !== "suppression" && freq < 1) {
        return "Frequency must be a number greater than 0";
      }
      if (taskData.calendarBased) {
        if (freq < 1 || freq > 10000) return "Frequency must be between 1 and 10,000";
      }
    }
    return null;
  };

  const normalizedFrequencyInterval = useMemo(() => {
    const raw = taskData?.frequency;
    if (raw === undefined || raw === null) return "";
    return String(raw).replace(/[^0-9]/g, "");
  }, [taskData?.frequency]);

  useEffect(() => {
    if (!show) return;
    if (taskData?.schedulingType !== "cycle") return;

    setCycleRows((prev) => {
      if (!prev.length) return prev;
      const next = prev.map((row) => {
        if (row.interval === normalizedFrequencyInterval) return row;
        return { ...row, interval: normalizedFrequencyInterval };
      });
      return next;
    });
  }, [normalizedFrequencyInterval, show, taskData?.schedulingType]);

  const handleCycleRowChange = useCallback((clientId, field, value) => {
    setCycleRows((prev) =>
      prev.map((row) => {
        if (row.clientId !== clientId) return row;

        if (field === "interval") {
          const sanitized = typeof value === "string" ? value.replace(/[^0-9]/g, "") : "";
          return { ...row, interval: sanitized };
        }

        if (field === "taskId") {
          return { ...row, taskId: value || null, meterId: null };
        }

        return { ...row, [field]: value };
      })
    );
    setCycleRowErrors((prev) => {
      if (!prev[`${clientId}-${field}`]) return prev;
      const next = { ...prev };
      delete next[`${clientId}-${field}`];
      if (next.general) {
        delete next.general;
      }
      return next;
    });
  }, []);

  const handleCycleDragEnd = useCallback((result) => {
    if (!result.destination) return;
    if (result.source.index === result.destination.index) return;
    setCycleRows((prev) => {
      if (!prev.length) return prev;
      const next = [...prev];
      const [moved] = next.splice(result.source.index, 1);
      next.splice(result.destination.index, 0, moved);
      return next;
    });
  }, []);

  const handleAddCycleRow = useCallback(() => {
    setCycleRows((prev) => {
      if (!prev.length) {
        return buildDefaultCycleRows();
      }
      return [...prev, createCycleRow({ interval: normalizedFrequencyInterval })];
    });
    setCycleRowErrors((prev) => {
      if (!prev.general) return prev;
      const next = { ...prev };
      delete next.general;
      return next;
    });
  }, [buildDefaultCycleRows, normalizedFrequencyInterval]);

  const handleDeleteCycleRow = useCallback((clientId) => {
    setCycleRows((prev) => {
      if (prev.length <= 1) {
        return prev;
      }
      const next = prev.filter((row) => row.clientId !== clientId);
      return next.length ? next : prev;
    });
  }, []);

  const validateCycleConfiguration = useCallback(() => {
    if (taskData?.schedulingType !== "cycle") {
      setCycleRowErrors({});
      setCycleFormErrors({});
      return true;
    }

    const isGroupValid = validateCycleGroupField();
    const nextErrors = {};
    const generalMessages = [];

    if (cycleRows.length < 2) {
      generalMessages.push("Add at least two tasks to create a cycle.");
    }

    if (!baseMeterId && taskData.calendarBased === 0) {
      generalMessages.push("Select a meter to configure the cycle.");
    }

    const requiredTaskValue =
      taskData?.id !== undefined && taskData?.id !== null
        ? String(taskData.id)
        : CURRENT_TASK_PLACEHOLDER;
    const includesCurrentTask = cycleRows.some((row) => {
      if (!row.taskId) return false;
      if (requiredTaskValue === CURRENT_TASK_PLACEHOLDER) {
        return row.taskId === CURRENT_TASK_PLACEHOLDER;
      }
      if (row.taskId === CURRENT_TASK_PLACEHOLDER) {
        return false;
      }
      return Number(row.taskId) === Number(requiredTaskValue);
    });

    if (!includesCurrentTask) {
      generalMessages.push("Include the task you are editing in the cycle");
    }

    cycleRows.forEach((row) => {
      if (!row.taskId) {
        nextErrors[`${row.clientId}-taskId`] = "Select a task";
      }
      const intervalValue = Number(row.interval);
      if (!Number.isInteger(intervalValue) || intervalValue <= 0) {
        nextErrors[`${row.clientId}-interval`] = "Enter a number";
      }
    });

    if (generalMessages.length) {
      nextErrors.general = generalMessages.join(" ");
    }

    setCycleRowErrors(nextErrors);
    return isGroupValid && Object.keys(nextErrors).length === 0;
  }, [
    baseMeterId,
    cycleRows,
    setCycleFormErrors,
    taskData?.calendarBased,
    taskData?.id,
    taskData?.schedulingType,
    validateCycleGroupField,
  ]);

  const buildCycleConfigurationPayload = useCallback(() => {
    if (taskData?.schedulingType !== "cycle") {
      return null;
    }

    const trimmedName = (cycleForm.name || "").trim();
    if (!trimmedName || !cycleRows.length) {
      return null;
    }

    return {
      cycleGroupId: taskData?.cycleGroupId ?? null,
      name: trimmedName,
      rows: cycleRows.map((row, index) => {
        const rawTaskId = row.taskId;
        const resolvedTaskId =
          rawTaskId === CURRENT_TASK_PLACEHOLDER ? CURRENT_TASK_PLACEHOLDER : rawTaskId;
        const numericTaskId =
          resolvedTaskId && resolvedTaskId !== CURRENT_TASK_PLACEHOLDER
            ? Number(resolvedTaskId)
            : taskData?.id ?? null;
        let resolvedMeterId = row.meterId ?? null;

        if (!resolvedMeterId) {
          if (resolvedTaskId === CURRENT_TASK_PLACEHOLDER) {
            resolvedMeterId = baseMeterId ?? null;
          } else if (numericTaskId && taskMeterMap.has(Number(numericTaskId))) {
            resolvedMeterId = taskMeterMap.get(Number(numericTaskId)) ?? null;
          } else if (numericTaskId && taskData?.assetMeterId) {
            resolvedMeterId = Number(taskData.assetMeterId);
          } else if (baseMeterId) {
            resolvedMeterId = baseMeterId;
          }
        }

        return {
          id: row.id ?? null,
          taskId: resolvedTaskId ?? null,
          interval: Number(row.interval) || null,
          order: index + 1,
          meterId: resolvedMeterId ?? null,
        };
      }),
    };
  }, [
    baseMeterId,
    cycleForm.name,
    cycleRows,
    taskData?.assetMeterId,
    taskData?.cycleGroupId,
    taskData?.id,
    taskData?.schedulingType,
    taskMeterMap,
  ]);

  const handleSave = async () => {
    const fieldErrors = {
      ...validateForm(dynamicTaskFormFields, taskData),
      ...validateForm(schedulingFields, taskData),
    };
    const rawLastDone = taskData?.lastDone;
    if (
      Number(taskData?.calendarBased) !== 1 &&
      rawLastDone !== undefined &&
      rawLastDone !== null &&
      String(rawLastDone).trim() !== ""
    ) {
      const parsedLastDone = Number(rawLastDone);
      if (!Number.isInteger(parsedLastDone) || parsedLastDone < 0) {
        fieldErrors.lastDone = "Last Known Change must be a non-negative integer";
      } else if (
        typeof latestMeterReading === "number" &&
        Number.isFinite(latestMeterReading) &&
        latestMeterReading > 0 &&
        parsedLastDone >= latestMeterReading
      ) {
        fieldErrors.lastDone = `Last Known Change must be less than the latest meter reading (${latestMeterReading})`;
      }
    }
    if (taskData.planningTask) {
      const freqError = validateFrequency();
      if (freqError) {
        fieldErrors.frequency = freqError;
      }
    }

    setErrors(fieldErrors);
    if (Object.keys(fieldErrors).length > 0) return;

    const isCycleValid = validateCycleConfiguration();
    if (!isCycleValid) return;

    try {
      setLoading(true);
      const payload = Object.fromEntries(
        Object.entries(taskData).filter(
          ([key, value]) => typeof value !== "object" || value === null || key === "taskFiles"
        )
      );
      payload.planningTask = taskData.planningTask ? 1 : 0;
      payload.calendarBased = taskData.calendarBased ? 1 : 0;
      payload.schedulingGroupId = taskData.schedulingGroupId ?? null;
      payload.assetMeterId = taskData.assetMeterId ?? null;
      delete payload.code;
      delete payload.codeLabel;
      delete payload.modifierLabel;
      if (Number(taskData?.calendarBased) === 1) {
        payload.lastDoneDate = payload.lastDone ? new Date(payload.lastDone).toISOString() : null;
        delete payload.lastDone;
      } else {
        delete payload.lastDoneDate;
        if (payload.lastDone === "" || payload.lastDone === undefined) {
          delete payload.lastDone;
        } else {
          const parsedLastDone = Number(payload.lastDone);
          if (Number.isFinite(parsedLastDone)) {
            payload.lastDone = parsedLastDone;
          } else {
            delete payload.lastDone;
          }
        }
      }
      const cycleConfigurationPayload = buildCycleConfigurationPayload();
      if (cycleConfigurationPayload) {
        payload.cycleConfiguration = cycleConfigurationPayload;
      }

      if (taskData?.schedulingType === "suppression" && suppressionGroupTasks.length) {
        payload.suppressionGroupTaskUpdates = suppressionGroupTasks.map((groupTask) => ({
          id: groupTask.id,
          schedulingWindow: Number(groupTask.schedulingWindow),
          schedulingPriority: Number(groupTask.schedulingPriority),
        }));
      }
      await updateTasks(taskId, payload);
      if (onSaved) {
        await onSaved();
      } else {
        onClose();
      }
    } catch (err) {
      console.error("Failed to save task", err);
    } finally {
      setLoading(false);
    }
  };

  if (!show) return null;

  return (
    <>
       <div className="modal modal-backdrop show d-block" tabIndex="-1" role="dialog" style={{ zIndex: 1090 }}>
        <div className="modal-dialog modal-xl" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title d-flex align-items-center gap-2">
                Edit Task
              </h5>
              <button type="button" className="btn-close" onClick={onClose} />
            </div>
            <div className="modal-body">
              <ul className="nav nav-tabs mb-3">
                <li className="nav-item">
                  <button
                    className={`nav-link ${activeTab === "general" ? "active" : ""}`}
                    onClick={() => setActiveTab("general")}
                  >
                    General
                    {renderErrorDot(generalTabHasError)}
                  </button>
                </li>
                <li className="nav-item">
                  <button
                    className={`nav-link ${activeTab === "scheduling" ? "active" : ""}`}
                    onClick={() => setActiveTab("scheduling")}
                  >
                    Scheduling
                    {renderErrorDot(schedulingTabHasError)}
                  </button>
                </li>
              </ul>
              {activeTab === "general" && (
                <div className="row g-3 mb-24">
                  {dynamicTaskFormFields.reduce((fields, field) => {
                    if (field.field === "lastDone") {
                      return fields;
                    }
                    fields.push(renderGeneralField(field));
                    return fields;
                  }, [])}
                </div>
              )}
              {activeTab === "scheduling" && (
                <>
                  <div className="row g-3 mt-0">
                    {schedulingFields
                      .filter((field) => field.field === "schedulingType")
                      .map(renderSchedulingField)}
                    {[schedulingFields.find((field) => field.field === "forecastBasis")]
                      .filter(Boolean)
                      .map(renderSchedulingField)}
                    <div className="col-md-2" key="lastDone-input">
                      <label className="form-label">{lastDoneLabel}</label>
                      <input
                        type={Number(taskData?.calendarBased) === 1 ? "date" : "number"}
                        inputMode={Number(taskData?.calendarBased) === 1 ? undefined : "numeric"}
                        step={Number(taskData?.calendarBased) === 1 ? undefined : "1"}
                        min={Number(taskData?.calendarBased) === 1 ? undefined : "0"}
                        className={`form-control ${errors.lastDone ? "is-invalid" : ""}`}
                        value={taskData?.lastDone ?? ""}
                        onChange={handleLastDoneChange}
                        disabled={isLastDoneLocked}
                        autoComplete="off"
                      />
                      {errors.lastDone && (
                        <div className="invalid-feedback">{errors.lastDone}</div>
                      )}
                    </div>
                    <div className="col-md-2 d-flex align-items-end" key="lastDone-toggle">
                      <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm ms-2 d-flex align-items-center gap-1"
                        style={{ position: "relative", top: "-7px" }}
                        onClick={handleLastDoneToggle}
                        title={isLastDoneLocked ? "Unlock last known change" : "Lock last known change"}
                      >
                        <Icon icon="mdi:lock-open-outline" className="icon text-xl line-height-1" />
                        {isLastDoneLocked ? "Unlock" : "Lock"}
                      </button>
                    </div>
                  </div>
                  <div className="row g-3 mt-0">
                    {schedulingFields
                      .filter((field) => !["schedulingType", "forecastBasis"].includes(field.field))
                      .map(renderSchedulingField)}
                  </div>
                  {taskData?.schedulingType === "suppression" && (
                    <div className="mt-3">
                      <h6 className="text-lg pt-16 mb-16">Other Tasks in this Suppression Group</h6>
                      {!suppressionGroupTasks.length ? (
                        <div className="text-muted small">
                          No other tasks for this asset in the selected scheduling group.
                        </div>
                      ) : (
                        <div className="table-responsive">
                          <table className="table table-sm align-middle">
                            <thead>
                              <tr>
                                <th className="border-0 text-muted" style={{ minWidth: "260px" }}>Task Name</th>
                                <th className="border-0 text-muted" style={{ minWidth: "160px" }}>Scheduling Window</th>
                                <th className="border-0 text-muted" style={{ minWidth: "160px" }}>Scheduling Priority</th>
                              </tr>
                            </thead>
                            <tbody>
                              {suppressionGroupTasks.map((groupTask) => (
                                <tr key={groupTask.id}>
                                  <td className="border-0">
                                    <input
                                      type="text"
                                      className="form-control"
                                      value={groupTask.name}
                                      disabled
                                    />
                                  </td>
                                  <td className="border-0">
                                    <select
                                      className="form-select"
                                      value={groupTask.schedulingWindow}
                                      onChange={(event) =>
                                        handleSuppressionGroupTaskChange(
                                          groupTask.id,
                                          "schedulingWindow",
                                          event.target.value,
                                        )
                                      }
                                    >
                                      {schedulingWindowOptions.map((option) => (
                                        <option key={`${groupTask.id}-window-${option.value}`} value={option.value}>
                                          {option.label}
                                        </option>
                                      ))}
                                    </select>
                                  </td>
                                  <td className="border-0">
                                    <select
                                      className="form-select"
                                      value={groupTask.schedulingPriority}
                                      onChange={(event) =>
                                        handleSuppressionGroupTaskChange(
                                          groupTask.id,
                                          "schedulingPriority",
                                          event.target.value,
                                        )
                                      }
                                    >
                                      {schedulingPriorityOptions.map((option) => (
                                        <option key={`${groupTask.id}-priority-${option.value}`} value={option.value}>
                                          {option.label}
                                        </option>
                                      ))}
                                    </select>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  )}
                  {taskData?.schedulingType === "cycle" && (
                    <div className="mt-4">
                      <div className="row g-3 mt-16">{cycleGroupFields.map(renderCycleGroupField)}</div>
                      {isCycleLoading ? (
                        <div className="d-flex align-items-center gap-2 mt-3">
                          <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                          <span>Loading cycle tasks…</span>
                        </div>
                      ) : (
                        <CycleTasksEditor
                          calendarBased={taskData?.calendarBased}
                          cycleRows={cycleRows}
                          cycleRowErrors={cycleRowErrors}
                          cycleTaskOptions={cycleTaskOptions}
                          baseTaskLabel={baseTaskLabel}
                          baseMeterId={baseMeterId}
                          isCycleLoading={isCycleLoading}
                          onDragEnd={handleCycleDragEnd}
                          onRowChange={handleCycleRowChange}
                          onDeleteRow={handleDeleteCycleRow}
                          onAddRow={handleAddCycleRow}
                        />
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={onClose}>
                Cancel
              </button>
              <button className="btn btn-primary" onClick={handleSave} disabled={loading}>
                Save
              </button>
            </div>
          </div>
        </div>
      </div>
      {showLastDoneWarning && (
        <>
          <div className="modal-backdrop fade show" style={{ zIndex: 1060 }}></div>
          <div className="modal fade show d-block" tabIndex="-1" style={{ zIndex: 1061 }}>
            <div className="modal-dialog" style={{ top: "20vh" }}>
              <div className="modal-content">
                <div className="modal-body">
                  <h6 className="fw-semibold">
                    <Icon icon="mdi:block" className="icon text-danger" style={{ position: "relative", top: "-3px" }} />
                    <span className="ms-8">Warning</span>
                  </h6>
                  <p className="mb-0">
                    The Last Known Reading records the last component change before the asset was added to Samurai. If the
                    component is being changed now, use a work order instead. Either create one directly or use Change
                    Component option on the Component Management page.
                  </p>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => {
                      setIsLastDoneLocked(true);
                      setShowLastDoneWarning(false);
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => setShowLastDoneWarning(false)}
                  >
                    Ok, I Understand
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

TaskEditModal.propTypes = {
  taskId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  show: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func,
  showCostFields: PropTypes.bool,
};

export default TaskEditModal;
